import React from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const RadarChartResults = ({ results, questionnaireConfig }) => {
  if (!results || !results.scores) {
    return <div>Données de résultats non disponibles</div>;
  }

  const { scores } = results;
  const top10Scores = scores.slice(0, 10);

  // Prepare radar chart data for top 10
  const radarLabels = top10Scores.map(score => score.name);
  const radarDataValues = top10Scores.map(score => score.score);

  const radarData = {
    labels: radarLabels,
    datasets: [
      {
        label: 'Vos Dons Spirituels',
        data: radarDataValues,
        backgroundColor: `${questionnaireConfig.color}33`,
        borderColor: questionnaireConfig.color,
        borderWidth: 2,
        pointBackgroundColor: questionnaireConfig.color,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: questionnaireConfig.color,
      },
    ],
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: {
          display: true,
        },
        suggestedMin: 0,
        suggestedMax: questionnaireConfig.scaleMax,
        pointLabels: {
          font: {
            size: 11,
          },
        },
        ticks: {
          stepSize: 0.5,
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${context.raw.toFixed(2)}`;
          }
        }
      }
    },
  };

  // Calculate percentages
  const maxPossibleScore = questionnaireConfig.scaleMax;
  
  return (
    <div className="radar-chart-results">
      {/* Top 10 Results Table */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
          Vos 10 Dons Spirituels Principaux
        </h3>
        <div className="table-responsive">
          <table className="results-table">
            <thead>
              <tr>
                <th>Rang</th>
                <th>Don Spirituel</th>
                <th>Score</th>
                <th>Pourcentage</th>
              </tr>
            </thead>
            <tbody>
              {top10Scores.map((score, index) => {
                const percentage = ((score.score / maxPossibleScore) * 100).toFixed(1);
                return (
                  <tr key={score.category}>
                    <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                      {index + 1}
                    </td>
                    <td style={{ fontWeight: 'bold' }}>{score.name}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span
                        style={{
                          backgroundColor: 
                            score.score >= 2.5 ? '#27ae60' :
                            score.score >= 2.0 ? '#f39c12' : '#e74c3c',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                        }}
                      >
                        {score.score.toFixed(2)}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{
                        background: 'linear-gradient(to right, #3498db 0%, #3498db ' + percentage + '%, #ecf0f1 ' + percentage + '%, #ecf0f1 100%)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        color: '#2c3e50',
                      }}>
                        {percentage}%
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Radar Chart for Top 10 */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '1rem', textAlign: 'center' }}>
          Graphique Radar - Top 10 Dons Spirituels
        </h3>
        <div style={{ height: '500px', padding: '1rem' }}>
          <Radar data={radarData} options={radarOptions} />
        </div>
      </div>

      {/* All Results Table */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
          Tous vos Dons Spirituels
        </h3>
        <div className="table-responsive">
          <table className="results-table">
            <thead>
              <tr>
                <th>Rang</th>
                <th>Don Spirituel</th>
                <th>Score</th>
                <th>Pourcentage</th>
                <th>Évaluation</th>
              </tr>
            </thead>
            <tbody>
              {scores.map((score, index) => {
                const percentage = ((score.score / maxPossibleScore) * 100).toFixed(1);
                return (
                  <tr key={score.category}>
                    <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                      {index + 1}
                    </td>
                    <td style={{ fontWeight: 'bold' }}>{score.name}</td>
                    <td style={{ textAlign: 'center' }}>
                      {score.score.toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {percentage}%
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {score.score >= 2.5 && (
                        <span style={{ color: '#27ae60', fontWeight: 'bold' }}>
                          ✓ Don fort
                        </span>
                      )}
                      {score.score >= 2.0 && score.score < 2.5 && (
                        <span style={{ color: '#f39c12', fontWeight: 'bold' }}>
                          ~ Don modéré
                        </span>
                      )}
                      {score.score < 2.0 && (
                        <span style={{ color: '#e74c3c' }}>
                          - Don faible
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Interpretation Guide */}
      <div className="card">
        <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
          Guide d'interprétation
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '1rem'
        }}>
          <div style={{ 
            backgroundColor: '#e8f5e8', 
            padding: '1rem', 
            borderRadius: '8px',
            borderLeft: '4px solid #27ae60'
          }}>
            <h4 style={{ color: '#27ae60', margin: '0 0 0.5rem 0' }}>Don fort (2.5-3.0)</h4>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Ce don vous caractérise fortement. Explorez les opportunités de service qui utilisent ce don.
            </p>
          </div>
          <div style={{ 
            backgroundColor: '#fef9e7', 
            padding: '1rem', 
            borderRadius: '8px',
            borderLeft: '4px solid #f39c12'
          }}>
            <h4 style={{ color: '#f39c12', margin: '0 0 0.5rem 0' }}>Don modéré (2.0-2.4)</h4>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Ce don peut être développé avec de la pratique et de l'expérience.
            </p>
          </div>
          <div style={{ 
            backgroundColor: '#fdf2f2', 
            padding: '1rem', 
            borderRadius: '8px',
            borderLeft: '4px solid #e74c3c'
          }}>
            <h4 style={{ color: '#e74c3c', margin: '0 0 0.5rem 0' }}>Don faible (0-1.9)</h4>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Ce don ne vous caractérise pas naturellement, mais peut être développé si nécessaire.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RadarChartResults;